import { useCallback, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';

import { dialogsEndpoints } from '@/api/endpoints';
import FormDialog from '@/components/atoms/FormDialog/FormDialog';
import {
  FormAutocomplete,
  AutocompleteOption,
} from '@/components/ui/custom/Autocomplete/Autocomplete';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useCloseModal } from '@/hooks/useCloseModal';
import useDialogs from '@/hooks/useDialogs';
import { TextAction } from '@/shared/types/action';
import { Dialog as DialogType } from '@/shared/types/dialog';
import { LENGTH_S, LENGTH_XXL, MAX_DIALOG_NAME_LENGTH } from '@/util/validation/constants';
import { errorMessage } from '@/util/validation/helpers';
import { createNameFieldSchema } from '@/util/validation/schema-builders';

const cleanString = (str: string) => str?.replace(/[^a-zA-Z0-9 ]/g, ' ') ?? '';

interface Props {
  intentName: string;
  brainId: string;
}

function ModalConnectIntent({ intentName, brainId }: Props) {
  const { t } = useTranslation();
  const { dialogs, createDialog, createStatus } = useDialogs(brainId);
  const closeModal = useCloseModal();

  const dialogSchema = yup.object({
    dialog_name: createNameFieldSchema({
      getEndpoint: (name) => dialogsEndpoints.checkName(brainId, name, { brain_id: brainId }),
      max: MAX_DIALOG_NAME_LENGTH,
    }),
    trigger: yup.string().required(t('validation.required')).max(LENGTH_S),
    response: yup.string().max(LENGTH_XXL),
    collection: yup.string().nullable(),
  });

  type FormType = yup.InferType<typeof dialogSchema>;

  const formMethods = useForm<FormType>({
    mode: 'onChange',
    defaultValues: {
      dialog_name: cleanString(intentName),
      trigger: intentName ?? '',
      response: '',
      collection: '',
    },
    resolver: yupResolver<FormType, unknown, FormType>(dialogSchema),
  });

  const {
    handleSubmit,
    getValues,
    setValue,
    control,
    formState: { errors, isValid },
  } = formMethods;

  const onSubmit = () => {
    const { dialog_name, trigger, response, collection } = getValues();
    const node_id = uuidv4();
    const actions = response
      ? [{ action_id: uuidv4(), type: 'text' as TextAction['type'], texts: [response], node_id }]
      : [];

    const dialog: Partial<DialogType> = {
      name: dialog_name,
      collection,
      nodes: [{ type: 'intent', intent: trigger, node_id, name: dialog_name, actions }],
    };
    createDialog({ dialog }, { onSuccess: closeModal });
  };

  const collectionOptions = useMemo<AutocompleteOption[]>(
    () =>
      [...new Set(dialogs?.map((d) => d.collection).filter((d) => !!d && d !== '_orphans'))].map(
        (value) => ({ value, label: value, category: t('subnav.folder') })
      ),
    [dialogs, t]
  );

  const handleCollectionChange = useCallback(
    (value: string) => setValue('collection', value),
    [setValue]
  );

  return (
    <FormDialog
      title={t('intent.create_dialog')}
      description={t('intent.create_dialog_subtitle')}
      submitLabel={t('common.create')}
      onSubmit={handleSubmit(onSubmit)}
      submitDisabled={!isValid}
      isLoading={createStatus === 'pending'}
    >
      <Form {...formMethods}>
        <form autoComplete="off" className="space-y-6">
          <FormField
            control={control}
            name="dialog_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('intent.dialog_name')}</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value ?? ''} />
                </FormControl>
                <FormMessage>{errorMessage({ field: errors.dialog_name })}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="trigger"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('intent.dialog_trigger')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ''}
                    readOnly
                    className="bg-muted/50 text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0"
                  />
                </FormControl>
                <FormMessage>{errorMessage({ field: errors.trigger })}</FormMessage>
              </FormItem>
            )}
          />
          <FormAutocomplete
            control={control}
            name="collection"
            options={collectionOptions}
            label={t('subnav.folder')}
            placeholder={t('subnav.folder_placeholder')}
            onChange={handleCollectionChange}
            error={!!errors.collection}
            errorMessage={errors.collection?.message}
          />
          <FormField
            control={control}
            name="response"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('intent.response_description')}</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    value={field.value ?? ''}
                    minRows={3}
                    maxRows={10}
                    placeholder={t('dialog.type_a_text_reply')}
                  />
                </FormControl>
                <FormMessage>{errorMessage({ field: errors.response })}</FormMessage>
              </FormItem>
            )}
          />
        </form>
      </Form>
    </FormDialog>
  );
}

export default ModalConnectIntent;
